import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import PLANES_CONFIG from "../config/planes.json";
import { db, functions } from "../services/firebase";
import { formatDate, toDate } from "./format";

// Cobro con Mercado Pago (functions/servicioCobros.js). El plan y la suscripción los escribe solo el servidor.
export const PRECIOS = PLANES_CONFIG.precios;
export const COBRO = PLANES_CONFIG.cobro;

export const SE_COBRA_EN_PESOS = "Se cobra en pesos al dólar oficial del día de cada cobro.";

// Checkbox obligatorio antes de pagar (mensual y anual). El servidor tampoco inicia el pago sin él.
export const ACEPTO_COTIZACION = "Acepto que el monto en pesos se recalcule según la cotización oficial del día del cobro.";

// Meses que ahorra el anual (129 = 10 × 12,90 y 89 = 10 × 8,90: ahorra 2).
export const MESES_DE_AHORRO = Math.round(12 - PRECIOS.anual / PRECIOS.mensual);

const usdFormat = new Intl.NumberFormat("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const pesosFormat = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 });

// 12.9 → "USD 12,90"; 129 → "USD 129".
export function formatUSD(n) {
  if (n == null) return "—";
  return `USD ${Number.isInteger(n) ? usdFormat.format(n) : n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// 20100 → "$ 20.100".
export const formatPesos = (n) => (n == null ? "—" : `$ ${pesosFormat.format(n)}`);

// Precios en pesos del día y lugares de fundador (publico/precios: lectura pública, lo escribe el servidor).
export function subscribeToPrices(onData, onError) {
  return onSnapshot(doc(db, "publico", "precios"), (snapshot) => onData(snapshot.exists() ? snapshot.data() : null), onError);
}

// Pagos propios, del más nuevo al más viejo (firestore.rules: cada usuario lee solo los suyos).
export function subscribeToMyPayments(uid, onData, onError) {
  return onSnapshot(
    query(collection(db, "pagos"), where("uid", "==", uid)),
    (snapshot) =>
      onData(
        snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (toDate(b.creadoEn)?.getTime() ?? 0) - (toDate(a.creadoEn)?.getTime() ?? 0))
      ),
    onError
  );
}

const call = (name) => async (data) => (await httpsCallable(functions, name)(data)).data;

// Devuelven { url } de Mercado Pago (en modo prueba, el link de sandbox): la app redirige ahí.
export const subscribeMonthly = call("suscribirMensual");
export const payAnnual = call("pagarAnual");
export const cancelSubscription = call("cancelarSuscripcion");

// Lo que muestra cada columna de /planes: el precio normal y, mientras queden lugares, el recuadro de fundador.
// precios: publico/precios (puede ser null mientras carga o si falla).
export function priceSummary(precios) {
  const p = precios || {};
  const quedan = p.fundadorQuedan ?? PRECIOS.fundador.lugares;
  return {
    mensualUSD: PRECIOS.mensual,
    mensualARS: p.mensualARS ?? null,
    anualUSD: PRECIOS.anual,
    anualARS: p.anualARS ?? null,
    fundador:
      quedan > 0
        ? {
            quedan,
            meses: PRECIOS.fundador.meses,
            mensualUSD: PRECIOS.fundador.mensual,
            mensualARS: p.fundadorARS ?? null,
            anualUSD: PRECIOS.fundador.anual,
            anualARS: p.anualFundadorARS ?? null,
          }
        : null,
  };
}

// Mismo cálculo que precioAnualUSD en functions/cobros.js (el servidor es el que decide el precio).
// Igual que sumarMeses en functions/vencimientos.js (en UTC; si el día no existe en el mes de llegada, el último).
function sumarMeses(msInicio, meses) {
  const d = new Date(msInicio);
  const dia = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + meses);
  const ultimoDia = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(dia, ultimoDia));
  return d.getTime();
}

export function precioAnualUSD({ fundadorNuevo = false, fundadorHasta = null, desde }) {
  if (fundadorHasta == null) return fundadorNuevo ? PRECIOS.fundador.anual : PRECIOS.anual;
  let mesesFundador = 0;
  for (let m = 0; m < 12; m++) if (sumarMeses(desde, m) < fundadorHasta) mesesFundador++;
  return Math.round(((mesesFundador * PRECIOS.fundador.anual + (12 - mesesFundador) * PRECIOS.anual) / 12) * 100) / 100;
}

// Monto en pesos estimado (USD × cotización, redondeado a la centena, como el servidor).
export const estimatePesos = (usd, cotizacion) => (cotizacion ? Math.round((usd * cotizacion) / PRECIOS.redondeoPesos) * PRECIOS.redondeoPesos : null);

// Precio del anual para quien está mirando. vigente: currentPlan(); s: su suscripción; precios: publico/precios.
export function myAnnualPrice({ vigente, s, esFundador, precios, now = Date.now() }) {
  const fundadorHasta = s?.tipo === "mensual" && s.fundador ? toDate(s.fundadorHasta)?.getTime() ?? null : null;
  // Fundador nuevo: si quedan lugares, o si ya tiene uno reservado por el mensual. Quien ya usó su año, no.
  const reservado = s?.tipo === "mensual" && s.fundador && fundadorHasta == null;
  const fundadorNuevo = fundadorHasta == null && !(esFundador && !reservado) && (reservado || (precios?.fundadorQuedan ?? 0) > 0);
  const desde = vigente && vigente.plan !== "gratis" && vigente.planVence && vigente.planVence > now ? vigente.planVence : now;
  const usd = precioAnualUSD({ fundadorNuevo, fundadorHasta, desde });
  return { usd, ars: estimatePesos(usd, precios?.cotizacion?.valor), fundador: fundadorNuevo || fundadorHasta != null };
}

export const SUSCRIPCION_ESTADOS = {
  pendiente: { label: "Esperando a Mercado Pago", tone: "amber" },
  activa: { label: "Activa", tone: "emerald" },
  en_gracia: { label: "Pago pendiente", tone: "rose" },
  cancelada: { label: "Cancelada", tone: "slate" },
};

// "Visa terminada en 4242", "Mastercard", null.
export function describePaymentMethod(medio) {
  if (!medio?.tipo) return null;
  const nombre = { visa: "Visa", master: "Mastercard", amex: "American Express", debvisa: "Visa Débito", debmaster: "Mastercard Débito", account_money: "Dinero en Mercado Pago" }[medio.tipo] || medio.tipo;
  return medio.ultimos4 ? `${nombre} terminada en ${medio.ultimos4}` : nombre;
}

// ¿Qué puede hacer el usuario con su suscripción? s: usuarios/{uid}.suscripcion (o undefined).
export function subscriptionActions(s) {
  const mensualViva = s?.tipo === "mensual" && ["activa", "en_gracia"].includes(s.estado);
  return {
    puedeSuscribirse: !mensualViva,
    puedeCancelar: mensualViva,
    puedePasarAAnual: mensualViva,
  };
}

// La suscripción está en los 5 días de gracia (un cobro falló y Mercado Pago lo reintenta).
export const inGrace = (s, now = Date.now()) => s?.estado === "en_gracia" && (toDate(s.graciaHasta)?.getTime() ?? 0) > now;

// Plan pago en curso (no la prueba de 14 días): { tipo: "anual" | "mensual", hasta, mensualProgramada } o null.
// Una mensual programada para después del anual (o cancelada antes de cobrar) cuenta como el anual que sigue vigente.
export function activePlan({ s, vigente, now = Date.now() }) {
  const hasta = vigente && vigente.plan !== "gratis" && !vigente.enPrueba ? vigente.planVence : null;
  if (!s || !hasta || hasta <= now) return null;
  const sinCobrar = !s.primerCobroEn;
  const mensualProgramada = s.tipo === "mensual" && s.estado === "activa" && sinCobrar && (toDate(s.proximoCobro)?.getTime() ?? 0) > now;
  if (s.tipo === "anual" && s.estado === "activa") return { tipo: "anual", hasta, mensualProgramada: false };
  if (s.tipo === "mensual" && s.despuesDelAnual && sinCobrar && ["activa", "pendiente", "cancelada"].includes(s.estado)) {
    return { tipo: "anual", hasta, mensualProgramada, mensualDesde: mensualProgramada ? toDate(s.proximoCobro) : null };
  }
  if (s.tipo === "mensual" && ["activa", "en_gracia"].includes(s.estado)) return { tipo: "mensual", hasta, mensualProgramada: false };
  return null;
}

// Qué hacer al tocar un botón de pago. tipo: "mensual" | "anual"; activo: activePlan(); planName: "Clínica";
// annualUSD: precio del anual para esa persona. Devuelve null (seguir sin preguntar), { mismoPlan: true } (no mostrar
// el botón: "Este es tu plan actual") o { mensaje } (pedir confirmación).
export function paymentConfirmation({ tipo, activo, planName, annualUSD }) {
  if (!activo) return null;
  if (activo.tipo === tipo) return { mismoPlan: true };
  const efecto = tipo === "mensual" ? `la mensual empieza a cobrar el ${formatDate(activo.hasta)}` : `pasás a anual por ${formatUSD(annualUSD)} y se cancela la mensual`;
  return { mensaje: `Ya tenés el plan ${planName} ${activo.tipo} activo hasta el ${formatDate(activo.hasta)}. Si seguís, ${efecto}. ¿Querés continuar?` };
}
